import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

interface CSVImportModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  endpoint: string;
  sampleFileUrl?: string;
  onSuccess?: (result: any) => void;
}

interface ImportResult {
  success: boolean;
  message: string;
  imported: number;
  skipped: number;
  errors: number;
  errorDetails?: string[];
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({
  open,
  onClose,
  title,
  endpoint,
  sampleFileUrl,
  onSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please select a valid CSV file');
        setFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`/api${endpoint}?dryRun=${dryRun}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        setError(data.message || 'Import failed');
      }
    } catch (err) {
      setError('Failed to upload file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDryRun(false);
    setResult(null);
    setError(null);
    onClose();
  };

  const downloadSample = () => {
    if (sampleFileUrl) {
      const link = document.createElement('a');
      link.href = sampleFileUrl;
      link.download = 'sample.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file to import data. The file should contain the required columns.
          </Typography>
          
          {sampleFileUrl && (
            <Box sx={{ mb: 2 }}>
              <Link href="#" onClick={downloadSample} underline="hover">
                Download sample CSV file
              </Link>
            </Box>
          )}

          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              {file ? file.name : 'Click to select CSV file'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or drag and drop
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
            }
            label="Dry run (preview import without saving)"
            sx={{ mt: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert 
              severity={result.success ? 'success' : 'warning'} 
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                {result.message}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Imported: {result.imported} | Skipped: {result.skipped} | Errors: {result.errors}
              </Typography>
              {result.errorDetails && result.errorDetails.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Error Details:
                  </Typography>
                  {result.errorDetails.slice(0, 5).map((detail, index) => (
                    <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {detail}
                    </Typography>
                  ))}
                  {result.errorDetails.length > 5 && (
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                      ... and {result.errorDetails.length - 5} more errors
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CSVImportModal; 